import { useRef, useEffect, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { usePdp } from '@context/PdpContext';
import {
  FitSizeValueInterface,
  SizeFitRiseInterface,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';
import { hasRenderableSizeChart } from '@utils/pdp/sizeChart/resolveSizeChart';
import DropdownMenu from '_components/UI/DropdownMenu';

import SelectionCell from '_components/UI/SelectionCell';
import {
  FEATURE_FLAG_PDP_2026_1,
  FEATURE_FLAG_PDP_SIZE_MERCHANDISING,
} from '@config/feature-flag';
import { MerchandisingSelectionConfig } from './types';
import styles from './SizeSelection.module.scss';

/** When more sizes exist than this, primary sizes render inside a dropdown. */
const SECONDARY_SIZE_DROPDOWN_THRESHOLD = 12;

const ProductMeasure = dynamic(
  () => import('@components/product/ProductMeasure/index'),
  { loading: () => null }
);

const FindMySize = dynamic(() => import('../FindMySize'), {
  loading: () => null,
});

const formatAxisLabel = (value: string, locale: string) =>
  value.length === 0
    ? ''
    : `${value[0].toLocaleUpperCase(locale)}${value.slice(1)}`;

export interface SizeSelectionProps {
  measure: SizeChartInterface;
  sizeDetail: SizeFitRiseInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  isBike: boolean;
  /**
   * Secondary size axis (legacy id-based; stories / tests).
   * Ignored when `merchandisingSelection` is set.
   */
  secondarySizes?: Array<{ id: number; name: string }>;
  selectedSecondarySizeId?: number | null;
  onSecondarySizeChange?: (id: number | null) => void;
  /** When set, replaces single size grid with two dynamic axes from merchandising. */
  merchandisingSelection?: MerchandisingSelectionConfig;
}

interface SizeGuideProps {
  hasSizeGuide: boolean;
  measure: SizeChartInterface;
  sizeDetail: SizeFitRiseInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  isBike: boolean;
}

const SizeGuide = ({
  hasSizeGuide,
  measure,
  sizeDetail,
  fitAndRise,
  isBike,
}: SizeGuideProps) => {
  if (!hasSizeGuide) return null;

  if (FEATURE_FLAG_PDP_2026_1) {
    return <FindMySize measure={measure} isBike={isBike} />;
  }

  return (
    <ProductMeasure
      measure={measure}
      sizeDetail={sizeDetail}
      fitAndRise={fitAndRise}
      isBike={isBike}
    />
  );
};

const SizeSelection = ({
  measure,
  sizeDetail,
  fitAndRise,
  isBike,
  secondarySizes,
  selectedSecondarySizeId,
  onSecondarySizeChange,
  merchandisingSelection,
}: SizeSelectionProps) => {
  const { t, i18n } = useTranslation('pdp');
  const containerRef = useRef<HTMLDivElement>(null);
  const primaryGridRef = useRef<HTMLDivElement>(null);
  const secondaryGridRef = useRef<HTMLDivElement>(null);
  const [primaryPillWidth, setPrimaryPillWidth] = useState<number | null>(null);
  const [secondaryPillWidth, setSecondaryPillWidth] = useState<number | null>(
    null
  );
  const [primaryDropdownOpen, setPrimaryDropdownOpen] = useState(false);

  const {
    sizes,
    selectedSizeId,
    setSelectedSizeId,
    addToCartClicked,
    setAddToCartClicked,
    isGiftCard,
    selectedSize,
  } = usePdp();

  useEffect(() => {
    if (addToCartClicked && !selectedSize) {
      containerRef.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [addToCartClicked, selectedSize]);

  const availableSizes = (sizes ?? []).filter((size) => size.isColourSelected);

  const hasOnlyOneSize = (sizes?.length ?? 0) === 1 && selectedSizeId != null;

  const handleSizeClick = (id: number) => {
    setSelectedSizeId(id);
    if (addToCartClicked) setAddToCartClicked(false);
    setPrimaryDropdownOpen(false);
  };

  const handleSecondarySizeClick = (id: number) => {
    onSecondarySizeChange?.(id);
  };

  const hasMerchandisingAxes =
    !!merchandisingSelection && FEATURE_FLAG_PDP_SIZE_MERCHANDISING;
  const hasLegacySecondary =
    !hasMerchandisingAxes && (secondarySizes?.length ?? 0) > 0;
  const merchandisingSecondaryValueCount =
    merchandisingSelection?.secondaryValues.length ?? 0;
  const usePrimarySizeDropdown = hasMerchandisingAxes
    ? false
    : !hasLegacySecondary &&
      availableSizes.length > SECONDARY_SIZE_DROPDOWN_THRESHOLD;
  const useMerchSecondarySizeDropdown =
    hasMerchandisingAxes &&
    merchandisingSecondaryValueCount > SECONDARY_SIZE_DROPDOWN_THRESHOLD;

  const showLegacyPrimaryError =
    !hasMerchandisingAxes &&
    addToCartClicked &&
    !selectedSizeId &&
    !hasOnlyOneSize;

  const showLegacySecondaryError =
    hasLegacySecondary && addToCartClicked && selectedSecondarySizeId == null;

  const showMerchPrimaryError =
    hasMerchandisingAxes &&
    addToCartClicked &&
    (merchandisingSelection?.primaryValues.length ?? 0) > 0 &&
    merchandisingSelection?.selectedPrimary == null;

  const showMerchSecondaryError =
    hasMerchandisingAxes &&
    addToCartClicked &&
    (merchandisingSelection?.secondaryValues.length ?? 0) > 0 &&
    merchandisingSelection?.selectedSecondary == null;

  const selectedSizeName =
    selectedSize?.name ??
    (hasOnlyOneSize ? availableSizes[0]?.name : null) ??
    null;

  const legacyPrimaryLabel = isGiftCard
    ? t('product.giftcard.amount')
    : `${t('product.size')} ${
        usePrimarySizeDropdown
          ? ''
          : selectedSizeName ?? t('product.size.select')
      }`;

  const selectedLegacySecondaryName =
    secondarySizes?.find((s) => s.id === selectedSecondarySizeId)?.name ?? null;
  const legacySecondaryLabel = `${t('product.length')} ${
    selectedLegacySecondaryName ?? t('product.length.select')
  }`;

  const merchPrimaryLabel = merchandisingSelection
    ? `${formatAxisLabel(
        merchandisingSelection.axisPrimaryLabel,
        i18n.language
      )}: ${merchandisingSelection.selectedPrimary ?? t('product.size.select')}`
    : '';

  const merchSecondaryLabel = merchandisingSelection
    ? `${formatAxisLabel(
        merchandisingSelection.axisSecondaryLabel,
        i18n.language
      )}: ${
        merchandisingSelection.selectedSecondary ?? t('product.size.select')
      }`
    : '';

  const hasSizeGuide = hasRenderableSizeChart(measure, isBike);

  const legacyPrimarySizeKey = availableSizes
    .map((size) => `${size.id}:${size.name}`)
    .join('|');
  const legacySecondarySizeKey = (secondarySizes ?? [])
    .map((size) => `${size.id}:${size.name}`)
    .join('|');

  const merchPrimaryKey = merchandisingSelection
    ? merchandisingSelection.primaryValues.join('|')
    : '';
  const merchSecondaryKey = merchandisingSelection
    ? merchandisingSelection.secondaryValues.join('|')
    : '';

  const primarySizeKey = hasMerchandisingAxes
    ? `merch-p:${merchPrimaryKey}`
    : legacyPrimarySizeKey;
  const secondarySizeKey = hasMerchandisingAxes
    ? `merch-s:${merchSecondaryKey}`
    : legacySecondarySizeKey;

  useEffect(() => {
    const getLargestPillWidth = (gridElement: HTMLDivElement | null) => {
      if (!gridElement) return null;

      const pillElements = Array.from(gridElement.querySelectorAll('button'));
      if (!pillElements.length) return null;

      const largestWidth = pillElements.reduce((maxWidth, element) => {
        const pillElement = element as HTMLButtonElement;
        const previousWidth = pillElement.style.width;
        pillElement.style.width = '';
        const measuredWidth = pillElement.offsetWidth;
        pillElement.style.width = previousWidth;

        return Math.max(maxWidth, measuredWidth);
      }, 0);

      return largestWidth || null;
    };

    const recalculatePillWidths = () => {
      setPrimaryPillWidth(getLargestPillWidth(primaryGridRef.current));
      setSecondaryPillWidth(getLargestPillWidth(secondaryGridRef.current));
    };

    recalculatePillWidths();
    window.addEventListener('resize', recalculatePillWidths);

    return () => {
      window.removeEventListener('resize', recalculatePillWidths);
    };
  }, [primarySizeKey, secondarySizeKey, primaryDropdownOpen]);

  if (!availableSizes.length) return null;

  const handleMerchPrimaryClick = (value: string) => {
    merchandisingSelection?.onPrimaryChange(value);
    if (addToCartClicked) setAddToCartClicked(false);
  };

  const handleMerchSecondaryClick = (value: string) => {
    merchandisingSelection?.onSecondaryChange(value);
    if (addToCartClicked) setAddToCartClicked(false);
  };

  if (hasMerchandisingAxes && merchandisingSelection) {
    const {
      primaryValues,
      secondaryValues,
      disabledPrimaryValues,
      disabledSecondaryValues,
      selectedPrimary,
      selectedSecondary,
    } = merchandisingSelection;

    return (
      <div
        className={styles.container}
        ref={containerRef}
        id="pdpSizeSelectContainer"
      >
        <div className={styles.axisBlock}>
          <div className={styles.labelRow}>
            <span
              className={styles.axisLabel}
              id="sizeSelectionLabel"
              data-testid="size-selection-label"
            >
              {merchPrimaryLabel}
            </span>
            <SizeGuide
              hasSizeGuide={hasSizeGuide}
              measure={measure}
              sizeDetail={sizeDetail}
              fitAndRise={fitAndRise}
              isBike={isBike}
            />
          </div>
          <div
            ref={primaryGridRef}
            className={styles.buttonGrid}
            role="group"
            aria-labelledby="sizeSelectionLabel"
            aria-describedby={
              showMerchPrimaryError ? 'sizeSelectionPrimaryError' : undefined
            }
          >
            {primaryValues.map((value) => (
              <SelectionCell
                key={`merch-primary-${value}`}
                selected={value === selectedPrimary}
                disabled={disabledPrimaryValues.includes(value)}
                onClick={() => handleMerchPrimaryClick(value)}
                style={
                  primaryPillWidth
                    ? { width: `${primaryPillWidth}px` }
                    : undefined
                }
              >
                {value}
              </SelectionCell>
            ))}
          </div>
          {showMerchPrimaryError && (
            <p
              id="sizeSelectionPrimaryError"
              className={styles.errorMessage}
              role="alert"
            >
              {t('product.size.error.required')}
            </p>
          )}
        </div>

        {secondaryValues.length > 0 && (
          <div className={styles.axisBlock}>
            <div className={styles.labelRow}>
              <span
                className={styles.axisLabel}
                id="secondarySizeSelectionLabel"
                data-testid="merchandising-secondary-label"
              >
                {merchSecondaryLabel}
              </span>
            </div>
            {useMerchSecondarySizeDropdown ? (
              <DropdownMenu
                id="secondarySizeSelectionSelect"
                classNames={styles.sizeSelect}
                defaultText={t('product.select.size')}
                selectedOption={selectedSecondary ?? undefined}
                data={secondaryValues.map((value) => ({
                  name: value,
                  value,
                  disabled: disabledSecondaryValues.includes(value),
                }))}
                handleChange={handleMerchSecondaryClick}
                hasError={showMerchSecondaryError}
                describedBy={
                  showMerchSecondaryError
                    ? 'sizeSelectionSecondaryError'
                    : undefined
                }
              />
            ) : (
              <div
                ref={secondaryGridRef}
                className={styles.buttonGrid}
                role="group"
                aria-labelledby="secondarySizeSelectionLabel"
                aria-describedby={
                  showMerchSecondaryError
                    ? 'sizeSelectionSecondaryError'
                    : undefined
                }
              >
                {secondaryValues.map((value) => (
                  <SelectionCell
                    key={`merch-secondary-${value}`}
                    selected={value === selectedSecondary}
                    disabled={disabledSecondaryValues.includes(value)}
                    error={showMerchSecondaryError}
                    onClick={() => handleMerchSecondaryClick(value)}
                    style={
                      secondaryPillWidth
                        ? { width: `${secondaryPillWidth}px` }
                        : undefined
                    }
                  >
                    {value}
                  </SelectionCell>
                ))}
              </div>
            )}
            {showMerchSecondaryError && (
              <p
                id="sizeSelectionSecondaryError"
                className={styles.errorMessage}
                role="alert"
              >
                {t('product.size.error.required')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      ref={containerRef}
      id="pdpSizeSelectContainer"
    >
      <div className={styles.axisBlock}>
        <div className={styles.labelRow}>
          <span
            className={styles.axisLabel}
            id="sizeSelectionLabel"
            data-testid="size-selection-label"
          >
            {legacyPrimaryLabel}
          </span>
          <SizeGuide
            hasSizeGuide={hasSizeGuide}
            measure={measure}
            sizeDetail={sizeDetail}
            fitAndRise={fitAndRise}
            isBike={isBike}
          />
        </div>
        {usePrimarySizeDropdown ? (
          <DropdownMenu
            id="sizeSelectionSelect"
            classNames={styles.sizeSelect}
            defaultText={
              isGiftCard
                ? t('product.giftcard.amount.defaultText')
                : t('product.select.size')
            }
            selectedOption={selectedSizeId ?? undefined}
            data={availableSizes.map((size) => ({
              name: size.name,
              value: size.id,
              disabled: size.notAvailableForSelectedColour,
            }))}
            handleChange={(value) => handleSizeClick(Number(value))}
            hasError={showLegacyPrimaryError}
            describedBy={
              showLegacyPrimaryError ? 'sizeSelectionPrimaryError' : undefined
            }
          />
        ) : (
          <div
            ref={primaryGridRef}
            className={styles.buttonGrid}
            role="group"
            aria-labelledby="sizeSelectionLabel"
            aria-describedby={
              showLegacyPrimaryError ? 'sizeSelectionPrimaryError' : undefined
            }
          >
            {availableSizes.map((size) => (
              <SelectionCell
                key={size.id}
                selected={size.id === selectedSizeId || hasOnlyOneSize}
                onClick={() => handleSizeClick(size.id)}
                disabled={size.notAvailableForSelectedColour}
                style={
                  primaryPillWidth
                    ? { width: `${primaryPillWidth}px` }
                    : undefined
                }
              >
                {size.name}
              </SelectionCell>
            ))}
          </div>
        )}
        {showLegacyPrimaryError && (
          <p
            id="sizeSelectionPrimaryError"
            className={styles.errorMessage}
            role="alert"
          >
            {t('product.size.error.required')}
          </p>
        )}
      </div>

      {hasLegacySecondary && (
        <div className={styles.axisBlock}>
          <div className={styles.labelRow}>
            <span className={styles.axisLabel} id="secondarySizeSelectionLabel">
              {legacySecondaryLabel}
            </span>
          </div>
          <div
            ref={secondaryGridRef}
            className={styles.buttonGrid}
            role="group"
            aria-labelledby="secondarySizeSelectionLabel"
            aria-describedby={
              showLegacySecondaryError
                ? 'sizeSelectionSecondaryError'
                : undefined
            }
          >
            {(secondarySizes ?? []).map((size) => (
              <SelectionCell
                key={size.id}
                selected={size.id === selectedSecondarySizeId}
                error={showLegacySecondaryError}
                onClick={() => handleSecondarySizeClick(size.id)}
                style={
                  secondaryPillWidth
                    ? { width: `${secondaryPillWidth}px` }
                    : undefined
                }
              >
                {size.name}
              </SelectionCell>
            ))}
          </div>
          {showLegacySecondaryError && (
            <p
              id="sizeSelectionSecondaryError"
              className={styles.errorMessage}
              role="alert"
            >
              {t('product.size.error.required')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SizeSelection;
